import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthProvider";
import { onServerWaking } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Already signed in? Skip the login form and go straight to the app.
  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [waking, setWaking] = useState(false);
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState(null);

  useEffect(() => onServerWaking(setWaking), []);

  useEffect(() => {
    if (sessionStorage.getItem("flash_verified")) {
      setFlash({ ok: true, text: "✓ Email verified — please sign in." });
      sessionStorage.removeItem("flash_verified");
    } else if (sessionStorage.getItem("flash_verify_error")) {
      setFlash({ ok: false, text: "That verification link is invalid or has expired. Sign in, or request a new link." });
      sessionStorage.removeItem("flash_verify_error");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password, remember);
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail ||
          (e2?.response
            ? "Login failed"
            : "Couldn't reach the server — it may be waking up. Please wait a moment and try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell" data-testid="login-page">
      <form className="login-card" onSubmit={submit}>
        <div className="login-logo">
          <span className="logo-icon">⬡</span>
          <span className="app-name">GovCo<span>.AI</span></span>
        </div>
        <h2>Sign in to your portal</h2>
        <p className="sub">AI-powered Government Solicitation Response Platform</p>

        {flash && (
          <div
            data-testid="login-flash"
            style={{
              margin: "4px 0 12px", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `1px solid ${flash.ok ? "#86efac" : "#fca5a5"}`,
              background: flash.ok ? "#f0fdf4" : "#fef2f2",
              color: flash.ok ? "#166534" : "#991b1b",
            }}
          >
            {flash.text}
          </div>
        )}

        <label htmlFor="email">Email</label>
        <input id="email" data-testid="login-email-input" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        <label htmlFor="password">Password</label>
        <input id="password" data-testid="login-password-input" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />

        <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontWeight: 400, fontSize: 12 }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ width: "auto" }} />
            Remember me
          </label>
          <Link to="/forgot-password" style={{ fontSize: 12, color: "var(--primary)", fontWeight: 600 }}>Forgot password?</Link>
        </div>

        {err && <div className="err" data-testid="login-error">{err}</div>}
        {loading && waking && (
          <div className="sub" style={{ marginTop: 10, fontSize: 12 }} data-testid="login-waking">
            ⏳ Waking up the server (free tier sleeps when idle) — this can take up to a minute…
          </div>
        )}

        <button type="submit" className="btn btn-primary w-full" style={{ justifyContent: "center", marginTop: 16 }}
          disabled={loading} data-testid="login-submit-btn">
          {loading ? <span className="spinner white" /> : "Sign In"}
        </button>

        <div style={{ textAlign: "center", marginTop: 16, fontSize: 13 }}>
          New here? <Link to="/register" style={{ color: "var(--primary)", fontWeight: 600 }}>Create an account</Link>
        </div>
      </form>
    </div>
  );
}
